import { ArrowForwardIcon, Box, ListItemButton, ListItemText, makeStyles, Theme } from '@freighthub/silhouette-core'
import { cargoString, IQuoteSheet } from 'features/BookingFlow'
import { portOrAddressToString } from 'features/Directory'
import { TransportModeIcon } from 'features/TransportPlan'
import moment from 'moment'
import { FC, MouseEvent } from 'react'
import { useI18n } from 'utils/localisation'

const useStyles = makeStyles<Theme>((theme) => ({
  root: {
    height: theme.spacing(7),
    padding: theme.spacing(0, 2),
    cursor: 'pointer',
    overflow: 'hidden',
    '&:not(:last-child)': {
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    '&:hover, &:focus': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  dot: {
    margin: theme.spacing(0, 0.75),
  },
  arrow: {
    height: 18,
    width: 18,
    margin: theme.spacing(0, 0.5),
  },
  icon: {
    height: '1.25rem',
    width: '1.25rem',
    marginLeft: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  primary: {
    display: 'flex',
    alignItems: 'center',
    fontWeight: theme.typography.fontWeightMedium,
  },
  secondary: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  multiline: {
    margin: 0,
  },
}))

interface RecentQuotesListItemProps {
  quoteSheet: IQuoteSheet
  onClick: (event: MouseEvent<HTMLDivElement>, quoteSheet: IQuoteSheet) => void
}

export const RecentQuotesListItem: FC<RecentQuotesListItemProps> = ({ quoteSheet, onClick }) => {
  const classes = useStyles({})
  const localisationStore = useI18n()
  const { journey, modes, cargo } = quoteSheet.request
  const { origin, destination } = journey
  const timeSinceQuoted = moment(quoteSheet.createdAt).fromNow()

  const primaryText = (
    <>
      <Box component="span" display="flex" alignItems="center" overflow="hidden">
        <Box component="span" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
          {portOrAddressToString(origin)}
        </Box>
        <ArrowForwardIcon className={classes.arrow} />
        <Box component="span" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
          {portOrAddressToString(destination)}
        </Box>
      </Box>
      {modes.map((mode) => (
        <TransportModeIcon key={mode} transportMode={mode} className={classes.icon} />
      ))}
    </>
  )

  const secondaryText = (
    <>
      <span>{cargo.map((c) => cargoString(c, localisationStore))}</span>
      <span className={classes.dot}>·</span>
      <span>{timeSinceQuoted}</span>
    </>
  )

  return (
    <ListItemButton
      onClick={(event) => onClick(event, quoteSheet)}
      className={classes.root}
      data-userlane-id="recent-quotes-item"
    >
      <ListItemText
        primary={primaryText}
        secondary={secondaryText}
        primaryTypographyProps={{
          color: 'textPrimary',
          variant: 'body2',
        }}
        secondaryTypographyProps={{
          color: 'textSecondary',
          variant: 'caption',
        }}
        classes={{
          primary: classes.primary,
          secondary: classes.secondary,
          multiline: classes.multiline,
        }}
      />
    </ListItemButton>
  )
}
